<template>
    <transition name="modal">
        <div id="file-manager-file-picker">
            <div class="modal-header file-manager-modal-header">
                <top-toolbar
                        :current-file="currentFile"
                        :current-path="currentPath"
                        :mime-types="mimeTypes"
                        :write-access="writeAccess"
                        @reload-folder="loadFolder( currentPath )"
                        @open-modal-create-folder="showCreateFolderModal = true"
                        @open-modal-delete-item="showDeleteItemModal = true"
                        @open-modal-move-item="showMoveItemModal = true"
                        @open-modal-rename-item="showRenameItemModal = true"
                        @upload-file="uploadFile"/>
            </div>

            <div id="FileManagerDropZone">

                <div v-if="loading" class="file-manager-alternative-content loading">
                    <p>{{ t.__('Ładowanie') }}...</p>
                    <h4 v-if="uploadProgress > 0">{{ uploadProgress }} %</h4>
                </div>

                <div v-else>

                    <div class="file-manager-file-browser">
                        <div class="row">
                            <div class="col-xs-12">

                                <div class="btn-group btn-group-sm breadcrumbs" role="group">
                                    <button type="button" class="btn btn-light" :disabled="!parentFolder"
                                            @click="loadFolder(parentFolder)">
                                        <i class="fa fa-arrow-up" aria-hidden="true"></i>
                                    </button>
                                    <button type="button"
                                            class="btn btn-light"
                                            v-for="(name, key) in breadCrumbs"
                                            :key="name" @click="loadFolder(key)"
                                            v-html="name">
                                    </button>
                                    <button type="button" class="btn btn-light" v-html="folderName"></button>
                                </div>

                            </div>
                        </div>

                        <div v-if="isFolderEmpty" class="file-manager-alternative-content">
                            <h4>{{ t.__('Ten katalog nie zawiera żadnych elementów') }}</h4>
                            <p class="d-none d-sm-inline-block">{{ t.__('Aby przesłać pliki kliknij przycisk lub przeciągnij i upuść pliki tutaj') }}</p>
                        </div>

                        <div v-else class="row">
                            <div v-bind:class="{ 'col-lg-8 col-md-6 col-sm-6': modal, 'col-lg-9 col-md-7 col-sm-6': !modal }">

                                <div class="table-responsive file-manager-file-picker-list">

                                    <table class="table table-condensed table-vmiddle">
                                        <thead>
                                        <tr class="heading-row">
                                            <th>
                                                <a href="javascript:void(0);" @click="orderFilesBy('name')">{{ t.__('Nazwa') }}</a>
                                            </th>
                                            <th class="d-none d-sm-none d-md-none d-lg-table-cell">
                                                <a href="javascript:void(0);" @click="orderFilesBy('mimeType')">{{ t.__('Typ') }}</a>
                                            </th>
                                            <th class="d-none d-sm-none d-md-table-cell">
                                                <a href="javascript:void(0);" @click="orderFilesBy('size')">{{ t.__('Rozmiar') }}</a>
                                            </th>
                                            <th class="d-none d-sm-none d-md-table-cell">
                                                <a href="javascript:void(0);" @click="orderFilesBy('dimensions')">{{ t.__('Wymiary') }}</a>
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr v-for="folder in folders"
                                            :key="folder.name"
                                            class="noselect"
                                            :class="[ (folder == currentFile) ? 'bg-primary' : '' ]"
                                            @click="previewFile(folder)"
                                            @dblclick="loadFolder(folder.fullPath)"
                                            @keyup.enter="loadFolder(folder.fullPath)">
                                            <td class="text-truncate">
                                                <i class="fa fa-folder"></i>
                                                <span>{{ folder.name }}</span>
                                            </td>
                                            <td class="d-none d-sm-none d-md-none d-lg-table-cell text-truncate"></td>
                                            <td class="d-none d-sm-none d-md-table-cell text-truncate"></td>
                                            <td class="d-none d-sm-none d-md-table-cell text-truncate"></td>
                                        </tr>
                                        <tr v-for="file in files"
                                            :key="file.name"
                                            class="noselect"
                                            :class="[ (file == currentFile) ? 'bg-primary' : '' ]"
                                            @click="previewFile(file)"
                                            @keyup.enter="selectFile(file)"
                                            @dblclick="selectFile(file)">
                                            <td class="text-truncate">
                                                <i v-if="isImage(file)" class="fa fa-image"></i>
                                                <i v-else class="fa fa-file"></i>
                                                <span>{{ file.name }}</span>
                                            </td>
                                            <td class="d-none d-sm-none d-md-none d-lg-table-cell text-truncate"> {{ file.mimeType }}
                                            </td>
                                            <td class="d-none d-sm-none d-md-table-cell text-truncate"> {{ file.size | humanFileSize }}
                                            </td>
                                            <td class="d-none d-sm-none d-md-table-cell text-truncate">
                                                <template v-if="file.dimensions && file.dimensions.width && file.dimensions.height">
                                                    {{ file.dimensions.width }} x {{ file.dimensions.height }} px
                                                </template>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div v-bind:class="{ 'col-lg-4 col-md-6 col-sm-6': modal, 'col-lg-3 col-md-5 col-sm-6': !modal }">
                                <preview-sidebar :current-file="currentFile"/>
                            </div>

                        </div>
                    </div>
                </div>

                <div class="modal-footer vertical-center">
                    <div class="item-count d-none d-sm-flex">
                        <div v-if="!loading">
                            {{ itemsCount }}
                            <span v-if="itemsCount === 1">{{ t.__('element') }}</span>
                            <span v-else-if="[2, 3, 4].includes(itemsCount)">{{ t.__('elementy') }}</span>
                            <span v-else>{{ t.__('elementów') }}</span>
                        </div>
                    </div>

                    <!-- Buttons to be rendered if the file-manager is in a modal window-->
                    <div v-if="modal" class="buttons text-right">
                        <button v-show="modal === 'file' && currentFile && !isFolder(currentFile)"
                                type="button"
                                class="btn btn-primary"
                                @click="selectFile()">
                            {{ t.__('Wybierz plik') }}
                        </button>
                        <button v-show="modal === 'path' && this.currentPath !== undefined && this.currentPath !== '/'"
                                type="button"
                                class="btn btn-primary"
                                @click="selectPath()">
                            {{ t.__('Wybierz aktualną ścieżkę') }}
                        </button>
                        <button type="button"
                                class="btn btn-light"
                                @click="close()">
                            {{ t.__('Zamknij') }}
                        </button>
                    </div>
                </div>
            </div>

            <modal-create-folder
                    :current-path="currentPath"
                    :show="showCreateFolderModal"
                    @file-manager-modal-close="showCreateFolderModal = false"
                    @reload-folder="loadFolder( currentPath )"
            />

            <modal-delete-item
                    :current-path="currentPath"
                    :current-file="currentFile"
                    :show="showDeleteItemModal"
                    @file-manager-modal-close="showDeleteItemModal = false"
                    @reload-folder="loadFolder( currentPath )"
            />

            <modal-move-item
                    :current-path="currentPath"
                    :current-file="currentFile"
                    :show="showMoveItemModal"
                    @file-manager-modal-close="showMoveItemModal = false"
                    @reload-folder="loadFolder( currentPath )"
            />

            <modal-rename-item
                    :current-path="currentPath"
                    :current-file="currentFile"
                    :show="showRenameItemModal"
                    @file-manager-modal-close="showRenameItemModal = false"
                    @reload-folder="loadFolder( currentPath )"
            />

        </div>
    </transition>
</template>

<script>

    import {orderBy} from "lodash";
    import fileManagerMixin from "../mixins/file-manager-mixin";
    import RenameItemModal from "./subcomponents/RenameItemModal";
    import CreateFolderModal from "./subcomponents/CreateFolderModal";
    import ConfirmDeleteModal from "./subcomponents/ConfirmDeleteModal";
    import MoveItemModal from "./subcomponents/MoveItemModal";
    import PreviewSideBar from "./subcomponents/PreviewSideBar";
    import TopToolBar from "./subcomponents/TopToolBar";

    export default {
        components: {
            "modal-rename-item": RenameItemModal,
            "modal-create-folder": CreateFolderModal,
            "modal-delete-item": ConfirmDeleteModal,
            "modal-move-item": MoveItemModal,
            "preview-sidebar": PreviewSideBar,
            "top-toolbar": TopToolBar,
        },

        mixins: [fileManagerMixin],

        props: {
            /**
             * Is this instance of the media manager a modal window.
             * If so then this property is used to show the close
             * buttons at the top and bottom of the screen.
             */
            modal: {
                default: false,
                type: String
            },

            /**
             * Default route prefix
             */
            prefix: {
                default: "/admin/",
                type: String
            },

            /**
             * The event to be fired when selectItem() is called.
             * The actual event name emitted is prefixed w/
             * "file-manager-selected-" so to avoid
             * clashes w/ other events.
             */
            selectedEventName: {
                default: 0,
                type: Number
            },

            /**
             * If this instance is a modal window then this
             * property is used to show or hide the
             * modal window.
             */
            show: {
                default: false,
                type: Boolean
            },

            /**
             * Mime types
             */
            mimeTypes: {
                default: "",
                type: String
            },

            /**
             * Base path
             */
            basePath: {
                default: null,
                type: String|null
            },

            /**
             * Base path
             */
            disableWriteAccess: {
                default: false,
                type: Boolean
            }
        },

        data: function () {

            return {

                /**
                 * breadCrumbs for the current path that are used to go
                 * backwards through the directory tree.
                 */
                breadCrumbs: {},

                /**
                 * Parent folder path if exists
                 */
                parentFolder: false,

                /**
                 * The currently highlighted file
                 */
                currentFile: {},

                /**
                 * The current path that the media manager is displaying
                 */
                currentPath: "",

                /**
                 * All of the files in the current path
                 */
                files: [],

                /**
                 * The current path's folder name
                 */
                folderName: null,

                /**
                 * All of the sub folders in the current path
                 */
                folders: [],

                /**
                 * Property to show the loading indicator
                 */
                loading: true,

                /**
                 * Property to show upload progress
                 */
                uploadProgress: 0,

                /**
                 * Total files and folder count
                 */
                itemsCount: 0,

                /**
                 * properties to show and hide internal modal windows
                 */
                showCreateFolderModal: false,
                showDeleteItemModal: false,
                showMoveItemModal: false,
                showRenameItemModal: false,

                /**
                 * property to hold direction of column sorting
                 */
                sortDirection: false,

                writeAccess: true,
            };
        },

        computed: {
            isFolderEmpty() {
                return (this.files && this.folders) ? ((this.files.length + this.folders.length) === 0) : false
            }
        },

        mounted: function () {
            this.loadFolder(this.basePath);
            this.dragUpload();

            if (!this.prefix.endsWith("/")) {
                this.prefix = `${this.prefix}/`;
            }
        },

        methods: {
            /**
             * sort files and folders...
             * @param column
             */
            orderFilesBy(column) {
                this.sortDirection = !this.sortDirection;
                const order = (this.sortDirection) ? "desc" : "asc";
                this.files = orderBy(this.files, [column], [order]);
                this.folders = orderBy(this.folders, [column], [order]);
            },

            close() {
                this.breadCrumbs = {};
                this.currentFile = {};
                this.currentPath = "";
                this.files = {};
                this.folderName = "";
                this.folders = {};
                this.itemsCount = 0;
                this.$emit("file-manager-modal-close");
            },

            loadFolder(path) {
                this.uploadProgress = 0;
                if (!path) {
                    if (localStorage.getItem('FileManagerLastPath')) {
                        path = localStorage.getItem('FileManagerLastPath');
                    } else {
                        path = (this.currentPath) ? this.currentPath : "";
                    }
                }

                this.loading = true;
                this.currentFile = false;

                const data = {
                    'path': path,
                    'mime_types': this.mimeTypes
                };

                axios.get(route('file-manager.browse', data)).then(
                    (response) => {
                        let responseData = response.data.data;
                        this.breadCrumbs = responseData.breadcrumbs;
                        this.setParentFolder();
                        // this.currentFile = {};
                        this.currentPath = responseData.folder_path;
                        this.loading = false;
                        this.folders = responseData.content.folders;
                        this.files = responseData.content.files;
                        this.folderName = responseData.folder_name;
                        this.itemsCount = responseData.items_count;
                        if (this.disableWriteAccess) {
                            this.writeAccess = false;
                        } else {
                            this.writeAccess = responseData.write_access;
                        }
                        if (this.folders.length > 0) {
                            this.currentFile = this.folders[0];
                        } else if (this.files.length > 0) {
                            this.currentFile = this.files[0];
                        } else {
                            this.currentFile = {};
                        }
                        localStorage.setItem('FileManagerLastPath', path);
                    },
                    (response) => {
                        let responseData = response.data.data;
                        if (responseData.error) {
                            this.fileManagerNotify(responseData.error, 'danger');
                        }
                        this.loading = false;
                        this.currentFile = {};
                    }
                );
            },

            previewFile(file) {
                this.currentFile = file;
            },

            uploadFile(payload) {

                let fieldName = payload.name;
                let fileList = payload.files;

                /**
                 * Create a new form request object.
                 * Gather all of the files to be uploaded and append them to it.
                 * Attach the current path so the server knows where to upload the files to.
                 * Send a post request to the server...
                 */
                const form = new FormData();
                Array
                    .from(Array(fileList.length).keys())
                    .map(x => {
                        form.append(fieldName, fileList[x], fileList[x].name);
                    });

                form.append("folder", this.currentPath);

                this.loading = true;
                axios.post(route('file-manager.upload-files'), form, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        },
                        onUploadProgress: function (progressEvent) {
                            this.uploadProgress = parseFloat(Math.round(progressEvent.loaded / progressEvent.total * 100));
                        }.bind(this)
                    }
                ).then(
                    (response) => {
                        this.fileManagerNotify(response.data.message, 'success');
                        this.loadFolder(this.currentPath);
                    },
                    (response) => {
                        const error = response.data.message;
                        this.fileManagerNotify(error, 'danger');
                        this.loadFolder(this.currentPath);
                    }
                );

            },

            selectFile() {
                /**
                 * Only dispatch an event if a custom event has been defined
                 */
                if (this.selectedEventName) {
                    window.eventHub.$emit("file-manager-selected-" + this.selectedEventName, this.currentFile);
                }
            },

            selectPath() {
                /**
                 * Only dispatch an event if a custom event has been defined
                 */
                if (this.currentPath !== undefined && this.currentPath !== '/' && this.selectedEventName) {
                    window.eventHub.$emit("file-manager-selected-" + this.selectedEventName, this.currentPath);
                }
            },

            dragUpload() {
                let Dropzone = require('dropzone'); //eslint-disable-line
                Dropzone.autoDiscover = false;
                this.dropzone = new Dropzone("div#FileManagerDropZone", {
                    clickable: false,
                    createImageThumbnails: false,
                    dictDefaultMessage: "",
                    enqueueForUpload: true,
                    paramName: "files",
                    previewsContainer: null,
                    previewTemplate: "<span class=\"hidden\"></span>",
                    hiddenInputContainer: true,
                    uploadMultiple: true,
                    url: route('file-manager.upload-files'),
                    headers: {
                        "X-CSRF-TOKEN": window.axios.defaults.headers.common["X-CSRF-TOKEN"]
                    },
                    acceptedFiles: this.mimeTypes,

                    sending: (file, xhr, form) => {
                        this.loading = true;
                        form.append("folder", this.currentPath);
                    },

                    completemultiple: () => {
                        this.loading = false;
                        this.loadFolder(this.currentPath);
                    },

                    errormultiple: (files, response) => {
                        this.fileManagerNotify(response.error, 'danger');
                    },

                    successmultiple: (files, response) => {
                        this.fileManagerNotify(response.success, 'success');
                    },

                    totaluploadprogress: (uploadProgress) => {
                        this.uploadProgress = parseFloat(Math.round(uploadProgress * 100) / 100).toFixed(2);
                        if (this.uploadProgress < 100) {
                            this.loading = true;
                        } else {
                            this.uploadProgress = 0;
                            this.loading = false;
                        }
                    }
                });
            },

            setParentFolder() {
                if (Object.keys(this.breadCrumbs).length > 0) {
                    this.parentFolder = Object.keys(this.breadCrumbs)[Object.keys(this.breadCrumbs).length - 1];
                } else {
                    this.parentFolder = false;
                }
            }

        }
    };
</script>
